import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  Download,
  FileText,
  FileSpreadsheet,
  Image,
  Share2,
  Calendar,
  Filter,
  Settings,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ExportControlsProps {
  data: any;
  filters: any;
  onExport?: (type: string, options: ExportOptions) => Promise<void>;
}

interface ExportOptions {
  format: 'pdf' | 'excel' | 'csv' | 'png';
  includeCharts: boolean;
  includeSummary: boolean;
  includeDetails: boolean;
  dateRange?: { start: string; end: string };
  sections?: string[];
}

const exportFormats = [
  {
    value: 'pdf',
    label: 'PDF',
    description: 'Relatório completo com gráficos',
    icon: FileText,
    color: 'text-red-600',
  },
  {
    value: 'excel',
    label: 'Excel',
    description: 'Planilha com dados detalhados',
    icon: FileSpreadsheet,
    color: 'text-green-600',
  },
  {
    value: 'csv',
    label: 'CSV',
    description: 'Dados brutos para análise',
    icon: FileSpreadsheet,
    color: 'text-blue-600',
  },
  {
    value: 'png',
    label: 'Imagem',
    description: 'Captura de tela do dashboard',
    icon: Image,
    color: 'text-purple-600',
  },
];

const reportSections = [
  { id: 'overview', label: 'Visão Geral', default: true },
  { id: 'metrics', label: 'Métricas Principais', default: true },
  { id: 'funnel', label: 'Funil de Conversão', default: true },
  { id: 'trends', label: 'Tendências', default: true },
  { id: 'status', label: 'Distribuição por Status', default: true },
  { id: 'performance', label: 'Performance de Vendedores', default: false },
  { id: 'services', label: 'Análise de Serviços', default: false },
];

export function ExportControls({ data, filters, onExport }: ExportControlsProps) {
  const [exportProgress, setExportProgress] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    format: 'pdf',
    includeCharts: true,
    includeSummary: true,
    includeDetails: false,
    sections: reportSections.filter(s => s.default).map(s => s.id),
  });

  const handleExport = async (format: string) => {
    setIsExporting(true);
    setExportProgress(0);

    try {
      // Simulate export progress
      const progressInterval = setInterval(() => {
        setExportProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      // Call the export function
      await onExport?.(format, exportOptions);

      // Complete the progress
      setExportProgress(100);
      clearInterval(progressInterval);

      toast.success(`Relatório exportado com sucesso!`);

      // Reset after a delay
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
      }, 2000);

    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erro ao exportar relatório');
      setIsExporting(false);
      setExportProgress(0);
    }
  };

  const handleShare = async () => {
    try {
      const shareUrl = `${window.location.origin}${window.location.pathname}?${new URLSearchParams({
        ...filters,
        shared: 'true',
      }).toString()}`;

      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link compartilhável copiado para a área de transferência!');
    } catch (error) {
      toast.error('Erro ao copiar link');
    }
  };

  const toggleSection = (sectionId: string, checked: boolean) => {
    setExportOptions(prev => ({
      ...prev,
      sections: checked
        ? [...(prev.sections || []), sectionId]
        : (prev.sections || []).filter(id => id !== sectionId),
    }));
  };

  return (
    <div className="flex items-center gap-2">
      {/* Share Button */}
      <Button variant="outline" size="sm" onClick={handleShare}>
        <Share2 className="h-4 w-4 mr-2" />
        Compartilhar
      </Button>

      {/* Export Dropdown */}
      <Dialog>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button disabled={isExporting}>
              <Download className="h-4 w-4 mr-2" />
              {isExporting ? 'Exportando...' : 'Exportar'}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Formatos de Exportação</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {exportFormats.map((format) => (
              <DropdownMenuItem
                key={format.value}
                onClick={() => handleExport(format.value)}
                disabled={isExporting}
              >
                <div className="flex items-center gap-3 w-full">
                  <format.icon className={`h-4 w-4 ${format.color}`} />
                  <div className="flex-1">
                    <div className="font-medium">{format.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {format.description}
                    </div>
                  </div>
                </div>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DialogTrigger asChild>
              <DropdownMenuItem>
                <Settings className="h-4 w-4 mr-2" />
                Configurar Exportação
              </DropdownMenuItem>
            </DialogTrigger>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Export Configuration Dialog */}
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar Exportação</DialogTitle>
            <DialogDescription>
              Personalize o conteúdo e formato do relatório
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Format Selection */}
            <div className="space-y-2">
              <Label>Formato do Arquivo</Label>
              <Select
                value={exportOptions.format}
                onValueChange={(value: any) => setExportOptions(prev => ({ ...prev, format: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {exportFormats.map((format) => (
                    <SelectItem key={format.value} value={format.value}>
                      <div className="flex items-center gap-2">
                        <format.icon className={`h-4 w-4 ${format.color}`} />
                        {format.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Content Options */}
            <div className="space-y-3">
              <Label>Conteúdo do Relatório</Label>

              <div className="space-y-2">
                {exportOptions.format === 'pdf' && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeCharts"
                      checked={exportOptions.includeCharts}
                      onCheckedChange={(checked) =>
                        setExportOptions(prev => ({ ...prev, includeCharts: !!checked }))
                      }
                    />
                    <Label htmlFor="includeCharts" className="text-sm">
                      Incluir gráficos e visualizações
                    </Label>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="includeSummary"
                    checked={exportOptions.includeSummary}
                    onCheckedChange={(checked) =>
                      setExportOptions(prev => ({ ...prev, includeSummary: !!checked }))
                    }
                  />
                  <Label htmlFor="includeSummary" className="text-sm">
                    Incluir resumo executivo
                  </Label>
                </div>

                {(exportOptions.format === 'excel' || exportOptions.format === 'csv') && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeDetails"
                      checked={exportOptions.includeDetails}
                      onCheckedChange={(checked) =>
                        setExportOptions(prev => ({ ...prev, includeDetails: !!checked }))
                      }
                    />
                    <Label htmlFor="includeDetails" className="text-sm">
                      Incluir dados brutos detalhados
                    </Label>
                  </div>
                )}
              </div>
            </div>

            {/* Report Sections */}
            {exportOptions.format === 'pdf' && (
              <div className="space-y-3">
                <Label>Seções do Relatório</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {reportSections.map((section) => (
                    <div key={section.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={section.id}
                        checked={exportOptions.sections?.includes(section.id) || false}
                        onCheckedChange={(checked) => toggleSection(section.id, !!checked)}
                      />
                      <Label htmlFor={section.id} className="text-sm">
                        {section.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Export Progress */}
            {isExporting && (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Exportando relatório...</span>
                  <span>{exportProgress}%</span>
                </div>
                <Progress value={exportProgress} className="h-2" />
              </div>
            )}

            {/* Export Action */}
            <div className="flex gap-2 pt-4">
              <Button
                onClick={() => handleExport(exportOptions.format)}
                disabled={isExporting || !exportOptions.sections?.length}
                className="flex-1"
              >
                {isExporting ? (
                  <>
                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Exportando...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Exportar Relatório
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quick Actions */}
      <div className="flex items-center gap-2">
        {/* Date Range Badge */}
        <Badge variant="outline" className="text-xs">
          <Calendar className="h-3 w-3 mr-1" />
          {filters.startDate && format(new Date(filters.startDate), 'MMM/yyyy', { locale: ptBR })}
          {' - '}
          {filters.endDate && format(new Date(filters.endDate), 'MMM/yyyy', { locale: ptBR })}
        </Badge>

        {/* Filters Applied Badge */}
        {(filters.clientId || filters.categoryId || filters.userId) && (
          <Badge variant="secondary" className="text-xs">
            <Filter className="h-3 w-3 mr-1" />
            Filtros ativos
          </Badge>
        )}
      </div>
    </div>
  );
}